/**
 * Compare File Viewer
 * Full-screen split overlay for viewing files from both consult columns independently.
 * Clicking a file opens the overlay with that column's preview; the other column
 * shows its file list for independent browsing.
 * Panels are resizable via a drag handle between them.
 */
var CompareFileViewer = (function() {
  'use strict';

  var cssEscape = (typeof window !== 'undefined' && window.CSS && window.CSS.escape) ||
    function(value) {
      var s = String(value);
      var out = '';
      for (var i = 0; i < s.length; i++) {
        var c = s.charCodeAt(i);
        if (c === 0) { out += '\ufffd'; continue; }
        if ((c >= 0x01 && c <= 0x1f) || c === 0x7f) {
          out += '\\' + c.toString(16) + ' ';
          continue;
        }
        if (i === 0 && c >= 0x30 && c <= 0x39) {
          out += '\\' + s.charAt(i);
          continue;
        }
        if (i === 1 && c >= 0x30 && c <= 0x39 && s.charCodeAt(0) === 0x2d) {
          out += '\\' + s.charAt(i);
          continue;
        }
        if (i === 0 && c === 0x2d && s.length === 1) {
          out += '\\' + s.charAt(i);
          continue;
        }
        if (c >= 0x80 || c === 0x5f || c === 0x2d || (c >= 0x30 && c <= 0x39) ||
            (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a)) {
          out += s.charAt(i);
          continue;
        }
        out += '\\' + s.charAt(i);
      }
      return out;
    };

  var overlay = null;
  var initialized = false;
  var _handleClickRef = null;
  var _handleKeydownRef = null;
  var _handleOverlayClickRef = null;
  var _splitPanel = null;

  // ---- Rendering helpers delegated to FilePreviewRenderer ----

  function escapeHtml(str) {
    return FilePreviewRenderer.escapeHtml(str);
  }

  function renderFile(shell, name, url, mode) {
    FilePreviewRenderer.renderFile(shell, name, url, mode);
  }

  // ---- File list helpers ----

  function getFilesForColumn(column, section) {
    var safeColumn = cssEscape(column);
    var safeSection = cssEscape(section || '');
    var list = document.querySelector('[data-compare-file-list][data-compare-column="' + safeColumn + '"][data-compare-section="' + safeSection + '"]');
    if (!list) return [];
    return Array.prototype.slice.call(list.querySelectorAll('[data-compare-file-viewer]'));
  }

  function getAllFilesForColumn(column) {
    var safeColumn = cssEscape(column);
    var lists = document.querySelectorAll('[data-compare-file-list][data-compare-column="' + safeColumn + '"]');
    var allFiles = [];
    for (var i = 0; i < lists.length; i++) {
      var items = lists[i].querySelectorAll('[data-compare-file-viewer]');
      for (var j = 0; j < items.length; j++) {
        allFiles.push(items[j]);
      }
    }
    return allFiles;
  }

  function getItemData(item) {
    return {
      name: (item.getAttribute('data-file-viewer-name') || ''),
      url: (item.getAttribute('data-file-viewer-url') || ''),
      mode: (item.getAttribute('data-file-viewer-preview-mode') || 'unsupported')
    };
  }

  // ---- Panel resize helpers (delegated to SplitPanel) ----

  function addResizeShields() {
    if (!overlay) return;
    var shells = overlay.querySelectorAll('[data-compare-viewer-shell]');
    for (var i = 0; i < shells.length; i++) {
      var shield = document.createElement('div');
      shield.className = 'absolute inset-0 z-10';
      shield.setAttribute('data-compare-resize-shield', '');
      shells[i].style.position = 'relative';
      shells[i].appendChild(shield);
    }
  }

  function removeResizeShields() {
    if (!overlay) return;
    var shields = overlay.querySelectorAll('[data-compare-resize-shield]');
    for (var i = 0; i < shields.length; i++) {
      if (shields[i].parentNode) shields[i].parentNode.removeChild(shields[i]);
    }
    var shells = overlay.querySelectorAll('[data-compare-viewer-shell]');
    for (var j = 0; j < shells.length; j++) {
      shells[j].style.position = '';
    }
  }

  function createSplitPanel() {
    if (!window.SplitPanel || !overlay) return null;
    return new window.SplitPanel({
      minWidth: 280,
      storageKey: 'compareViewerSplitRatio',
      getContainer: function() { return overlay; },
      getLeft: function() { return overlay.querySelector('[data-compare-viewer-panel="current"]'); },
      getRight: function() { return overlay.querySelector('[data-compare-viewer-panel="past"]'); },
      getHandle: function() { return overlay.querySelector('[data-compare-viewer-split-handle]'); },
      collapseBelowMin: false,
      onDragStart: addResizeShields,
      onDragEnd: removeResizeShields
    });
  }

  function applyViewerRatio(ratio) {
    if (_splitPanel) _splitPanel.applyRatio(ratio);
  }

  // ---- Overlay construction ----

  function buildOverlay() {
    if (overlay && overlay.isConnected) return overlay;
    overlay = null;

    overlay = document.createElement('div');
    overlay.id = 'compare-file-viewer-overlay';
    overlay.className = 'fixed inset-0 z-50 hidden bg-gray-900/60';

    var panel = document.createElement('div');
    panel.className = 'flex h-full';

    // Left panel (current)
    var leftPanel = document.createElement('div');
    leftPanel.className = 'flex-1 flex flex-col bg-white overflow-hidden';
    leftPanel.style.minWidth = '280px';
    leftPanel.setAttribute('data-compare-viewer-panel', 'current');
    leftPanel.innerHTML =
      '<div class="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">' +
        '<div class="min-w-0">' +
          '<span class="text-xs font-medium uppercase tracking-wide text-meclinic-teal">Current Consultation</span>' +
          '<h3 class="text-sm font-semibold text-gray-900 truncate" data-compare-viewer-title="current">Files</h3>' +
        '</div>' +
        '<div class="flex items-center gap-2">' +
          '<button type="button" class="hidden inline-flex items-center justify-center rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50" data-compare-viewer-prev="current">Prev</button>' +
          '<span class="hidden text-xs text-gray-500" data-compare-viewer-pos="current"></span>' +
          '<button type="button" class="hidden inline-flex items-center justify-center rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50" data-compare-viewer-next="current">Next</button>' +
        '</div>' +
      '</div>' +
      '<div class="flex-1 overflow-auto p-4 flex items-center justify-center" data-compare-viewer-shell="current">' +
        '<p class="text-sm text-gray-500">Click a file from this consultation to preview.</p>' +
      '</div>' +
      '<div class="border-t border-gray-200 bg-gray-50 px-4 py-2 flex items-center gap-2">' +
        '<button type="button" class="hidden inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50" data-compare-viewer-back="current"><i class="fa fa-fw fa-arrow-left mr-1"></i> Files</button>' +
        '<a href="#" class="hidden inline-flex items-center justify-center rounded-md border border-transparent bg-meclinic-teal px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700" data-compare-viewer-download="current" download>Download</a>' +
      '</div>';

    // Drag handle between panels
    var splitHandle = document.createElement('div');
    splitHandle.setAttribute('data-compare-viewer-split-handle', '');
    splitHandle.className = 'w-1.5 shrink-0 cursor-col-resize bg-gray-300 hover:bg-meclinic-teal active:bg-teal-700 transition-colors flex items-center justify-center';
    splitHandle.title = 'Drag to resize panels';

    // Right panel (past)
    var rightPanel = document.createElement('div');
    rightPanel.className = 'flex-1 flex flex-col bg-white overflow-hidden';
    rightPanel.style.minWidth = '280px';
    rightPanel.setAttribute('data-compare-viewer-panel', 'past');
    rightPanel.innerHTML =
      '<div class="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">' +
        '<div class="min-w-0">' +
          '<span class="text-xs font-medium uppercase tracking-wide text-gray-500">Past Consultation</span>' +
          '<h3 class="text-sm font-semibold text-gray-900 truncate" data-compare-viewer-title="past">Files</h3>' +
        '</div>' +
        '<div class="flex items-center gap-2">' +
          '<button type="button" class="hidden inline-flex items-center justify-center rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50" data-compare-viewer-prev="past">Prev</button>' +
          '<span class="hidden text-xs text-gray-500" data-compare-viewer-pos="past"></span>' +
          '<button type="button" class="hidden inline-flex items-center justify-center rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50" data-compare-viewer-next="past">Next</button>' +
          '<button type="button" class="inline-flex items-center justify-center rounded-md border border-transparent px-2 py-1 text-gray-400 hover:text-gray-600 text-lg" data-compare-viewer-close>&times;</button>' +
        '</div>' +
      '</div>' +
      '<div class="flex-1 overflow-auto p-4 flex items-center justify-center" data-compare-viewer-shell="past">' +
        '<p class="text-sm text-gray-500">Click a file from this consultation to preview.</p>' +
      '</div>' +
      '<div class="border-t border-gray-200 bg-gray-50 px-4 py-2 flex items-center gap-2">' +
        '<button type="button" class="hidden inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50" data-compare-viewer-back="past"><i class="fa fa-fw fa-arrow-left"></i> Files</button>' +
        '<a href="#" class="hidden inline-flex items-center justify-center rounded-md border border-transparent bg-meclinic-teal px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700" data-compare-viewer-download="past" download>Download</a>' +
      '</div>';

    panel.appendChild(leftPanel);
    panel.appendChild(splitHandle);
    panel.appendChild(rightPanel);
    overlay.appendChild(panel);

    // Initialize split panel for drag resize
    _splitPanel = createSplitPanel();
    if (_splitPanel) _splitPanel.init();

    document.body.appendChild(overlay);
    return overlay;
  }

  // ---- State ----

  var state = {
    past: { files: [], index: -1, section: '' },
    current: { files: [], index: -1, section: '' }
  };

  function loadFilesForColumn(column, section) {
    state[column].files = getFilesForColumn(column, section);
    state[column].section = section;
    state[column].index = -1;
  }

  function showNavControls(column, visible) {
    if (!overlay) return;
    var prevBtn = overlay.querySelector('[data-compare-viewer-prev="' + column + '"]');
    var posEl = overlay.querySelector('[data-compare-viewer-pos="' + column + '"]');
    var nextBtn = overlay.querySelector('[data-compare-viewer-next="' + column + '"]');
    var downloadBtn = overlay.querySelector('[data-compare-viewer-download="' + column + '"]');
    var backBtn = overlay.querySelector('[data-compare-viewer-back="' + column + '"]');

    var method = visible ? 'remove' : 'add';
    if (prevBtn) prevBtn.classList[method]('hidden');
    if (posEl) posEl.classList[method]('hidden');
    if (nextBtn) nextBtn.classList[method]('hidden');
    if (downloadBtn) downloadBtn.classList[method]('hidden');
    if (backBtn) backBtn.classList[method]('hidden');
  }

  function renderForColumn(column) {
    var s = state[column];
    var shell = overlay.querySelector('[data-compare-viewer-shell="' + column + '"]');
    var title = overlay.querySelector('[data-compare-viewer-title="' + column + '"]');
    var pos = overlay.querySelector('[data-compare-viewer-pos="' + column + '"]');
    var download = overlay.querySelector('[data-compare-viewer-download="' + column + '"]');
    var prevBtn = overlay.querySelector('[data-compare-viewer-prev="' + column + '"]');
    var nextBtn = overlay.querySelector('[data-compare-viewer-next="' + column + '"]');

    // No file selected for this column — show file list
    if (s.index < 0 || !s.files.length) {
      FilePreviewRenderer.clearNode(shell);
      renderFileList(shell, column);
      if (title) title.textContent = 'Files';
      if (pos) pos.textContent = '';
      showNavControls(column, false);
      return;
    }

    // File selected — show preview
    var idx = s.index;
    if (idx >= s.files.length) idx = s.files.length - 1;
    s.index = idx;

    var data = getItemData(s.files[idx]);
    if (title) title.textContent = data.name || 'File preview';
    if (pos) pos.textContent = (idx + 1) + ' of ' + s.files.length;
    if (download) {
      download.classList.remove('hidden');
      download.href = data.url;
      var safeName = (data.name || '').replace(/[^a-zA-Z0-9._\- ]/g, '_').replace(/\.{2,}/g, '.');
      download.setAttribute('download', safeName);
    }
    if (prevBtn) { prevBtn.classList.remove('hidden'); prevBtn.disabled = idx <= 0; }
    if (nextBtn) { nextBtn.classList.remove('hidden'); nextBtn.disabled = idx >= s.files.length - 1; }
    showNavControls(column, true);

    renderFile(shell, data.name, data.url, data.mode);
  }

  function renderFileList(shell, column) {
    var files = getAllFilesForColumn(column);
    if (!files.length) {
      shell.innerHTML = '<p class="text-sm text-gray-500">No files available.</p>';
      return;
    }

    var listHtml = '<div class="space-y-2 w-full">';
    listHtml += '<p class="text-xs text-gray-400 mb-2">Select a file to preview:</p>';
    for (var i = 0; i < files.length; i++) {
      var data = getItemData(files[i]);
      var section = files[i].getAttribute('data-compare-section') || '';
      var safeName = escapeHtml(data.name || 'File');
      var safeColumn = escapeHtml(column);
      var safeSection = escapeHtml(section);
      listHtml += '<button type="button" class="inline-flex max-w-full w-full items-start gap-2 rounded-md border border-gray-300 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-meclinic-teal" data-compare-viewer-list-item="' + i + '" data-compare-viewer-list-column="' + safeColumn + '" data-compare-viewer-list-section="' + safeSection + '">';
      listHtml += '<i class="fa fa-fw fa-paperclip mt-0.5"></i>';
      listHtml += '<span class="truncate">' + safeName + '</span>';
      listHtml += '</button>';
    }
    listHtml += '</div>';
    shell.innerHTML = listHtml;
  }

  function open(clickedItem) {
    var column = clickedItem.getAttribute('data-compare-column');
    var section = clickedItem.getAttribute('data-compare-section');

    buildOverlay();

    // Load files for the clicked column and navigate to the clicked item
    loadFilesForColumn(column, section);

    var clickedFiles = state[column].files;
    var clickedIdx = 0;
    for (var i = 0; i < clickedFiles.length; i++) {
      if (clickedFiles[i] === clickedItem) { clickedIdx = i; break; }
    }
    state[column].index = clickedIdx;

    // Other column: auto-open if that section has exactly one file
    var otherColumn = column === 'past' ? 'current' : 'past';
    var otherFiles = getFilesForColumn(otherColumn, section);
    if (otherFiles.length === 1) {
      loadFilesForColumn(otherColumn, section);
      state[otherColumn].index = 0;
    } else {
      state[otherColumn].index = -1;
      state[otherColumn].files = [];
      state[otherColumn].section = '';
    }

    renderForColumn('past');
    renderForColumn('current');

    // Make visible first so the browser computes dimensions,
    // then apply the split ratio on the next frame
    overlay.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');

    requestAnimationFrame(function() {
      var ratio = _splitPanel ? _splitPanel.getRatioFromStorage() : 0.5;
      applyViewerRatio(ratio);
    });
  }

  function close() {
    if (!overlay) return;
    overlay.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  }

  function navigate(column, delta) {
    var s = state[column];
    var newIdx = s.index + delta;
    if (newIdx < 0 || newIdx >= s.files.length) return;
    s.index = newIdx;
    renderForColumn(column);
  }

  function openFileFromList(column, listIndex) {
    var allFiles = getAllFilesForColumn(column);
    if (listIndex >= 0 && listIndex < allFiles.length) {
      // Load the section that contains this file so navigation (prev/next) works
      var section = allFiles[listIndex].getAttribute('data-compare-section') || '';
      loadFilesForColumn(column, section);

      // Find the same file within the section's file list
      var sectionFiles = state[column].files;
      for (var i = 0; i < sectionFiles.length; i++) {
        if (sectionFiles[i] === allFiles[listIndex]) {
          state[column].index = i;
          break;
        }
      }
    }
    renderForColumn(column);
  }

  // ---- Event handlers ----

  function handleClick(e) {
    var trigger = e.target.closest('[data-compare-file-viewer]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      open(trigger);
      return;
    }

    if (!overlay) return;

    var closeBtn = e.target.closest('[data-compare-viewer-close]');
    if (closeBtn) { close(); return; }

    var backBtn = e.target.closest('[data-compare-viewer-back]');
    if (backBtn) {
      var backColumn = backBtn.getAttribute('data-compare-viewer-back');
      state[backColumn].index = -1;
      renderForColumn(backColumn);
      return;
    }

    var prevBtn = e.target.closest('[data-compare-viewer-prev]');
    if (prevBtn) { navigate(prevBtn.getAttribute('data-compare-viewer-prev'), -1); return; }

    var nextBtn = e.target.closest('[data-compare-viewer-next]');
    if (nextBtn) { navigate(nextBtn.getAttribute('data-compare-viewer-next'), 1); return; }

    // File list item click inside overlay
    var listItem = e.target.closest('[data-compare-viewer-list-item]');
    if (listItem) {
      var column = listItem.getAttribute('data-compare-viewer-list-column');
      var listIdx = parseInt(listItem.getAttribute('data-compare-viewer-list-item'), 10);
      openFileFromList(column, listIdx);
      return;
    }
  }

  function handleKeydown(e) {
    var trigger = e.target.closest('[data-compare-file-viewer]');
    if (trigger && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      open(trigger);
      return;
    }

    if (!overlay || overlay.classList.contains('hidden')) return;

    if (e.key === 'Escape') { close(); return; }

    if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;

    var activePanel = document.activeElement && document.activeElement.closest('[data-compare-viewer-panel]');
    var column = activePanel ? activePanel.getAttribute('data-compare-viewer-panel') : null;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      navigate(column || 'current', -1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      navigate(column || 'current', 1);
    }
  }

  function reset() {
    if (overlay && overlay.isConnected) {
      close();
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }
    if (initialized) {
      document.removeEventListener('click', _handleClickRef);
      document.removeEventListener('keydown', _handleKeydownRef);
      document.removeEventListener('click', _handleOverlayClickRef);
    }
    if (_splitPanel) {
      _splitPanel.reset();
      _splitPanel = null;
    }
    overlay = null;
    initialized = false;
    _handleClickRef = null;
    _handleKeydownRef = null;
    _handleOverlayClickRef = null;
  }

  function init() {
    if (initialized) return;
    initialized = true;

    _handleClickRef = handleClick;
    _handleKeydownRef = handleKeydown;
    _handleOverlayClickRef = function(e) {
      if (overlay && e.target === overlay) close();
    };

    document.addEventListener('click', _handleClickRef);
    document.addEventListener('keydown', _handleKeydownRef);
    document.addEventListener('click', _handleOverlayClickRef);
  }

  return { init: init, reset: reset };
})();

document.addEventListener('turbolinks:load', function() {
  CompareFileViewer.reset();
  CompareFileViewer.init();
});
document.addEventListener('turbo:load', function() {
  CompareFileViewer.reset();
  CompareFileViewer.init();
});
document.addEventListener('turbo:before-cache', function() {
  CompareFileViewer.reset();
});

if (document.readyState !== 'loading') {
  CompareFileViewer.init();
}

window.CompareFileViewer = CompareFileViewer;
